import type { EffectsConfig } from '../config/effects.config';
import type { ScoringConfig } from '../config/scoring.config';
import type { PickupKind, ScoreSnapshot } from '../scoring/ScoreManager';
import { el, formatNumber } from './dom';
import { ICONS, icon } from './icons';
import type { SoundMode } from '../services/ProgressStore';
import { MusicToggle } from './MusicToggle';
import { ToastPriority, ToastQueue } from './ToastQueue';
import { SponsorBadge } from './SponsorBadge';
import type { AdSponsor } from '../advertising/AdProvider';

/** The ructec-Bulli dashboard: speed, meters left, load pieces, warning per lane from behind 0 … 1. */
export interface BusDashboard {
  readonly kmh: number;
  readonly left: number;
  readonly share: number;
  readonly cargo: number;
  readonly maxCargo: number;
  readonly rear: readonly number[];
}

export interface HudState {
  readonly score: ScoreSnapshot;
  readonly pursuitPressure: number;
  readonly pursuitCritical: boolean;
  /** Beer-crate turbo: remaining share 1 … 0 (0 = no turbo). */
  readonly turbo: number;
  /** ructec-Bulli dashboard while driving (null on foot). */
  readonly bus?: BusDashboard | null;
}

/** Pickup counters of the HUD (the toolbox stays out for now). Icons: public/assets/ui/hud/. */
const PICKUPS: readonly { readonly kind: PickupKind; readonly icon: string; readonly label: string }[] = [
  { kind: 'helmets', icon: '/assets/ui/hud/helm.png', label: 'Helme' },
  { kind: 'bottles', icon: '/assets/ui/hud/flasche.png', label: 'Flaschen' },
  { kind: 'crates', icon: '/assets/ui/hud/bierkiste.png', label: 'Kisten' },
];

interface CounterView {
  readonly value: HTMLSpanElement;
  last: number;
}

/**
 * In-run HUD, small and quiet (user 2026-10-03: "die Zähler sind zu groß … ein wenig dezenter"): top left lives and
 * points with pause and music below them, top right four counters – helmets, bottles (with the progress to the next
 * extra life), beer crates and distance; on phones the counters stand as a 2 × 2 block. Plus the turbo bar, the
 * pursuer warning and the notices.
 */
export class Hud {
  readonly element: HTMLDivElement;
  private readonly music: MusicToggle;
  private readonly hearts: HTMLDivElement;
  private readonly points: HTMLSpanElement;
  private readonly distance: HTMLSpanElement;
  private readonly counters = new Map<PickupKind, CounterView>();
  private readonly progress: HTMLSpanElement;
  private readonly turbo: HTMLDivElement;
  private readonly turboFill: HTMLSpanElement;
  private readonly pursuit: HTMLDivElement;
  private readonly toast: HTMLDivElement;
  private readonly toasts: ToastQueue;
  private shownToast: string | null = null;
  private lastLives = -1;
  private lastPoints = -1;
  private lastDistance = -1;

  constructor(
    parent: HTMLElement,
    private readonly scoring: ScoringConfig,
    effects: EffectsConfig,
    onPause: () => void,
    onToggleMusic: () => void,
  ) {
    this.toasts = new ToastQueue(effects.toastGapSeconds, effects.toastMaxPending);
    this.element = el('div', 'hud', undefined, parent);
    const top = el('div', 'hud-top', undefined, this.element);

    const left = el('div', 'hud-left', undefined, top);
    const status = el('div', 'hud-panel hud-status', undefined, left);
    const lives = el('div', 'hud-status-row', undefined, status);
    el('span', 'hud-label', 'Leben', lives);
    this.hearts = el('div', 'hud-hearts', undefined, lives);
    const points = el('div', 'hud-status-row', undefined, status);
    el('span', 'hud-label', 'Punkte', points);
    this.points = el('span', 'hud-value hud-points', '0', points);
    // signed in for the leaderboard (ructec 2026-10-08): short name and company under the points
    this.playerRow = el('div', 'hud-status-row hud-player', undefined, status);
    el('span', 'hud-label', 'Spieler', this.playerRow);
    this.playerName = el('span', 'hud-player-name', '', this.playerRow);
    this.playerRow.hidden = true;

    const buttons = el('div', 'hud-buttons', undefined, left);
    const pause = el('button', 'hud-panel hud-button hud-pause', undefined, buttons);
    pause.type = 'button';
    pause.setAttribute('aria-label', 'Pause');
    pause.append(icon(ICONS.pause, 'hud-button-icon'));
    pause.addEventListener('click', (event) => {
      event.stopPropagation();
      onPause();
    });
    // The tap on the button must not also count as a gesture (Confirm would resume immediately).
    pause.addEventListener('pointerdown', (event) => event.stopPropagation());
    this.music = new MusicToggle(buttons, 'hud-panel hud-button hud-music', onToggleMusic);

    // "sponsored by" the whole run long, under lives and points (ructec 2026-10-05)
    this.sponsor = new SponsorBadge(left, 'is-hud');

    const counters = el('div', 'hud-counters', undefined, top);
    let progress: HTMLSpanElement | null = null;
    for (const pickup of PICKUPS) {
      const counter = this.counter(counters, pickup.kind, pickup.label);
      const image = el('img', 'hud-counter-icon', undefined);
      image.src = pickup.icon;
      image.alt = '';
      image.decoding = 'async';
      counter.card.prepend(image);
      this.counters.set(pickup.kind, { value: counter.value, last: -1 });
      // progress to the next extra life along the bottom of the bottle counter
      if (pickup.kind === 'bottles') progress = el('span', 'hud-counter-progress', undefined, el('span', 'hud-counter-track', undefined, counter.card));
    }
    this.progress = progress ?? el('span');
    const distance = this.counter(counters, 'distance', 'Strecke');
    distance.card.prepend(icon(ICONS.distance, 'hud-counter-icon hud-counter-svg'));
    this.distance = distance.value;
    this.distance.textContent = '0 m';

    this.turbo = el('div', 'hud-turbo', undefined, this.element);
    el('span', 'hud-turbo-label', 'Turbo', this.turbo);
    this.turboFill = el('span', 'hud-turbo-fill', undefined, el('span', 'hud-turbo-track', undefined, this.turbo));

    this.pursuit = el('div', 'hud-pursuit', 'Die Verfolger holen auf!', this.element);
    this.toast = el('div', 'hud-toast', undefined, this.element);

    // ructec-Bulli dashboard (bottom centre): speed, meters left, the load on the roof;
    // above it a warning per lane for traffic coming from behind
    this.bus = el('div', 'hud-panel hud-bus', undefined, this.element);
    const gauge = el('div', 'hud-bus-gauge', undefined, this.bus);
    this.busSpeed = el('span', 'hud-bus-speed', '0', gauge);
    el('span', 'hud-label', 'km/h', gauge);
    const trip = el('div', 'hud-bus-trip', undefined, this.bus);
    this.busLeft = el('span', 'hud-bus-left', '1.500 m', trip);
    this.busFill = el('span', 'hud-bus-fill', undefined, el('span', 'hud-bus-track', undefined, trip));
    const load = el('div', 'hud-bus-load', undefined, this.bus);
    load.title = 'Ladung auf dem Dach';
    el('span', 'hud-label', 'Ladung', load);
    this.busCargoRow = el('div', 'hud-bus-cargo', undefined, load);
    const rear = el('div', 'hud-bus-rear', undefined, this.bus);
    this.busRear = Array.from({ length: 3 }, () => el('span', 'hud-bus-rear-lane', undefined, rear));
  }

  private readonly bus: HTMLDivElement;
  private readonly busSpeed: HTMLSpanElement;
  private readonly busLeft: HTMLSpanElement;
  private readonly busFill: HTMLSpanElement;
  private readonly busCargoRow: HTMLDivElement;
  private busCargo: HTMLSpanElement[] = [];
  private readonly busRear: HTMLSpanElement[];
  private lastBus = '';

  private readonly sponsor: SponsorBadge;
  private readonly playerRow: HTMLDivElement;
  private readonly playerName: HTMLSpanElement;

  /** Signed-in player (leaderboard): "MaxMus · Paradoxhaus" under the points; null hides the row. */
  setPlayer(player: { readonly company: string; readonly name: string } | null): void {
    this.playerName.textContent = player ? `${player.name} · ${player.company}` : '';
    this.playerName.title = this.playerName.textContent;
    this.playerRow.hidden = !player;
  }

  setVisible(visible: boolean): void {
    this.element.classList.toggle('is-hidden', !visible);
  }

  setSponsor(sponsor: AdSponsor | null): void {
    this.sponsor.set(sponsor);
  }

  setSoundMode(mode: SoundMode): void {
    this.music.setMode(mode);
  }

  /** Urgent notices replace the current one; info notices wait until it has run out (ToastQueue). */
  showToast(text: string, seconds = 1.6, priority: ToastPriority = ToastPriority.Urgent): void {
    this.toasts.push(text, seconds, priority);
    this.renderToast();
  }

  clearToasts(): void {
    this.toasts.clear();
    this.renderToast();
  }

  /** While a panel covers the screen (Bau-Quiz) notices wait unseen; their time stands still with the HUD's. */
  holdToasts(held: boolean): void {
    this.toast.classList.toggle('is-held', held);
  }

  update(dt: number, state: HudState): void {
    const { score } = state;
    // text only on change – the HUD updates every frame
    if (score.score !== this.lastPoints) {
      this.lastPoints = score.score;
      this.points.textContent = formatNumber(score.score);
    }
    const meters = Math.floor(score.distance);
    if (meters !== this.lastDistance) {
      this.lastDistance = meters;
      this.distance.textContent = `${formatNumber(meters)} m`;
    }
    for (const [kind, view] of this.counters) {
      const value = score.pickups[kind];
      if (value === view.last) continue;
      view.last = value;
      view.value.textContent = formatNumber(value);
    }
    this.progress.style.width = `${(score.bottleProgress / score.bottlesPerExtraLife) * 100}%`;
    if (score.extraLives !== this.lastLives) {
      this.lastLives = score.extraLives;
      this.hearts.setAttribute('aria-label', `${score.extraLives} Extra-Leben`);
      this.hearts.replaceChildren(
        ...Array.from({ length: this.scoring.maxExtraLives }, (_, i) => el('span', i < score.extraLives ? 'hud-heart is-full' : 'hud-heart')),
      );
    }
    this.turbo.classList.toggle('is-visible', state.turbo > 0);
    this.turboFill.style.width = `${Math.max(0, Math.min(1, state.turbo)) * 100}%`;
    this.pursuit.classList.toggle('is-visible', state.pursuitCritical);
    const bus = state.bus ?? null;
    this.bus.classList.toggle('is-visible', bus !== null);
    if (bus) {
      const key = `${Math.round(bus.kmh)}|${Math.ceil(bus.left / 10)}|${bus.cargo}|${bus.maxCargo}`;
      if (key !== this.lastBus) {
        this.lastBus = key;
        this.busSpeed.textContent = String(Math.round(bus.kmh));
        this.busLeft.textContent = `${formatNumber(Math.max(0, Math.ceil(bus.left / 10) * 10))} m`;
        if (this.busCargo.length !== bus.maxCargo) {
          this.busCargoRow.replaceChildren();
          this.busCargo = Array.from({ length: bus.maxCargo }, () => el('span', 'hud-bus-piece', undefined, this.busCargoRow));
        }
        this.busCargo.forEach((piece, i) => piece.classList.toggle('is-lost', i >= bus.cargo));
      }
      this.busFill.style.width = `${Math.max(0, Math.min(1, bus.share)) * 100}%`;
      this.busRear.forEach((lane, i) => {
        const level = bus.rear[i] ?? 0;
        lane.style.setProperty('--level', level.toFixed(2));
        lane.classList.toggle('is-on', level > 0);
      });
    }
    this.pursuit.style.setProperty('--pressure', state.pursuitPressure.toFixed(2));

    this.toasts.update(dt);
    this.renderToast();
  }

  /** One counter: (icon, added by the caller) value over a small label. */
  private counter(parent: HTMLElement, kind: string, label: string): { card: HTMLDivElement; value: HTMLSpanElement } {
    const card = el('div', `hud-panel hud-counter is-${kind}`, undefined, parent);
    card.title = label;
    const text = el('div', 'hud-counter-text', undefined, card);
    const value = el('span', 'hud-value', '0', text);
    el('span', 'hud-label', label, text);
    return { card, value };
  }

  private renderToast(): void {
    const text = this.toasts.text;
    if (text === this.shownToast) return;
    this.shownToast = text;
    // Keep the old text while fading out.
    if (text !== null) this.toast.textContent = text;
    this.toast.classList.toggle('is-visible', text !== null);
  }
}
